import React, { useState, useCallback, useEffect } from 'react';
import LayoutNav from './layout/LayoutNav';
import LayoutFooter from './layout/LayoutFooter';
import SectionHero from './sections/SectionHero';
import SectionStats from './sections/SectionStats';
import SectionProducts from './sections/SectionProducts';
import SectionWhyJungleBrick from './sections/SectionWhyJungleBrick';
import SectionServices from './sections/SectionServices';
import SectionProjects from './sections/SectionProjects';
import SectionCaseStudies from './sections/SectionCaseStudies';
import SectionWhoWeAre from './sections/SectionWhoWeAre';
import SectionContact from './sections/SectionContact';
import SectionConnect from './sections/SectionConnect';
import ServiceDetail from './pages/ServiceDetail';
import CaseStudyDetail from './pages/CaseStudyDetail';
import ProjectDetail from './pages/ProjectDetail';
import ProductDetailPage from './pages/ProductDetailPage';
import AboutPage from './pages/AboutPage';
import WorkPage from './pages/WorkPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import { useSiteContent } from '../hooks/useSiteContent';

function parseHash(hash) {
  const clean = (hash || '').replace(/^#/, '');
  if (
    clean === 'projects' ||
    clean === 'training' ||
    clean === 'all-training' ||
    clean === 'all-projects' ||
    clean === 'all-work' ||
    clean.startsWith('training/')
  ) {
    return { type: 'work' };
  }
  if (clean === 'products') return { type: 'home-products' };
  if (clean === 'all-casestudies') return { type: 'casestudies' };
  if (clean === 'about' || clean === 'whoweare') return { type: 'about' };
  if (clean === 'work') return { type: 'work' };
  if (clean === 'casestudies') return { type: 'casestudies' };
  if (clean.startsWith('product/')) {
    const parts = clean.slice(8).split('/');
    return { type: 'product', slug: parts[0] };
  }
  if (clean.startsWith('casestudy/')) return { type: 'casestudy', slug: clean.slice(10) };
  if (clean.startsWith('project/')) return { type: 'project', slug: clean.slice(8) };
  if (clean.startsWith('service/')) return { type: 'service', slug: clean.slice(8) };
  if (clean.startsWith('whitepaper/')) return { type: 'casestudy', slug: clean.slice(11) };
  return null;
}

/**
 * MainPage – Home + mega menus + dedicated pages.
 */
export default function MainPage() {
  const [activeSection, setActiveSection] = useState('');
  const [megaMenu, setMegaMenu] = useState(null); // services | products | casestudies | about
  const [hashRoute, setHashRoute] = useState(() => parseHash(window.location.hash));
  const [caseStudyItems, setCaseStudyItems] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const { content, loading, error } = useSiteContent();

  useEffect(() => {
    const handleHash = () => {
      const route = parseHash(window.location.hash);
      setHashRoute(route);
      if (route?.type === 'home-products') {
        window.location.hash = '#';
        setTimeout(() => {
          const el = document.getElementById('products');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveSection('products');
        }, 120);
        return;
      }
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', handleHash);
    handleHash();
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.PUBLIC_URL || ''}/case-studies/index.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json?.items) setCaseStudyItems(json.items);
      })
      .catch(() => {});
    fetch(`${process.env.PUBLIC_URL || ''}/projects/index.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json?.items) setWorkItems(json.items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    if (sectionId === 'work' || sectionId === 'casestudies' || sectionId === 'about') {
      window.location.hash = `#${sectionId}`;
      return;
    }
    if (window.location.hash && window.location.hash !== '#') {
      window.location.hash = '#';
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(sectionId);
      }, 120);
      return;
    }
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  }, []);

  const openMega = useCallback((id) => setMegaMenu(id), []);
  const closeMega = useCallback(() => setMegaMenu(null), []);

  const navigateToService = useCallback((slug) => {
    setMegaMenu(null);
    window.location.hash = `#service/${slug}`;
  }, []);

  const goHome = useCallback(() => {
    window.location.hash = '#';
  }, []);

  const goToContact = useCallback(() => {
    window.location.hash = '#';
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, []);

  const showServiceDetail = hashRoute?.type === 'service';
  const showCaseStudyDetail = hashRoute?.type === 'casestudy';
  const showProjectDetail = hashRoute?.type === 'project';
  const showProductDetail = hashRoute?.type === 'product';
  const showAbout = hashRoute?.type === 'about';
  const showWork = hashRoute?.type === 'work';
  const showCaseStudies = hashRoute?.type === 'casestudies';
  const showMain =
    !showServiceDetail &&
    !showCaseStudyDetail &&
    !showProjectDetail &&
    !showProductDetail &&
    !showAbout &&
    !showWork &&
    !showCaseStudies &&
    hashRoute?.type !== 'home-products';

  const navActive = showAbout
    ? 'about'
    : showCaseStudies || showCaseStudyDetail
      ? 'casestudies'
      : showProductDetail
        ? 'products'
        : showWork || showProjectDetail
          ? 'work'
          : activeSection;

  return (
    <div className="min-h-screen bg-forest-bg text-text-body">
      {error && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-sm text-amber-800">
          Could not load content from URL: {error}
        </div>
      )}
      <LayoutNav
        activeSection={navActive}
        content={content}
        megaMenu={megaMenu}
        onOpenMega={openMega}
        onCloseMega={closeMega}
        caseStudies={caseStudyItems}
        workItems={workItems}
        onNavigateToService={navigateToService}
        onGetStarted={goToContact}
        onContact={goToContact}
      />

      {showWork && <WorkPage onBack={goHome} onContact={goToContact} />}
      {showCaseStudies && <CaseStudiesPage onBack={goHome} onContact={goToContact} />}
      {showAbout && <AboutPage content={content} onBack={goHome} />}
      {showProductDetail && (
        <ProductDetailPage
          slug={hashRoute.slug}
          content={content}
          onBack={goHome}
          onContact={goToContact}
        />
      )}
      {showServiceDetail && (
        <ServiceDetail
          slug={hashRoute.slug}
          content={content}
          onBack={goHome}
          onOpenServices={() => openMega('services')}
          onContact={goToContact}
        />
      )}
      {showCaseStudyDetail && (
        <CaseStudyDetail
          slug={hashRoute.slug}
          onBack={() => {
            window.location.hash = '#casestudies';
          }}
        />
      )}
      {showProjectDetail && (
        <ProjectDetail
          slug={hashRoute.slug}
          onBack={() => {
            window.location.hash = '#work';
          }}
        />
      )}

      {showMain && (
        <main>
          {loading ? (
            <div className="flex items-center justify-center py-24 font-body text-text-sub">
              Loading…
            </div>
          ) : (
            <>
              <SectionHero
                onNavigate={scrollToSection}
                content={content}
              />
              <SectionProducts content={content?.products} />
              <SectionStats content={content?.stats} />
              <SectionWhyJungleBrick content={content?.whyJungleBrick} />
              <SectionServices
                content={content?.services}
                overlayContent={content?.servicesOverlay}
                onOpenServicesOverlay={() => openMega('services')}
                onNavigateToService={navigateToService}
              />
              <SectionProjects />
              <SectionCaseStudies />
              <SectionWhoWeAre
                content={content}
                onOpenAbout={() => openMega('about')}
                onGoAbout={() => {
                  window.location.hash = '#about';
                }}
              />
              <SectionConnect
                content={content?.connectBlock}
                onConnect={goToContact}
                onViewWork={() => {
                  window.location.hash = '#work';
                }}
              />
              <SectionContact content={content?.contact} />
            </>
          )}
        </main>
      )}

      <LayoutFooter onNavigate={scrollToSection} content={content?.footer} />
    </div>
  );
}
